import config from '../config';
import dayjs from 'dayjs';

/**
 * Print invoice/receipt with consistent design across the application
 * @param {Object} params - Print parameters
 * @param {Object} params.invoice - Invoice/income record to print
 * @param {Object} params.student - Student information (optional if already in invoice)
 * @param {Object} params.user - Current user with academy information
 * @param {Function} params.t - Translation function
 * @param {string} params.language - Current language (for RTL support)
 */
export const printInvoice = ({ invoice, student, user, t, language = 'en' }) => {
    const isRTL = language === 'ar' || language === 'ku';
    const studentInfo = student || invoice.student;
    // Refund / cashback records are stored with a negative amount
    const isRefund = invoice.category === 'refund';
    const refundAmount = Math.abs(invoice.amount || 0);

    // Construct full logo URLs before inserting into HTML
    const logoUrl = user?.academy?.logo ? `${config.baseUrl}${user.academy.logo}` : '';
    const watermarkUrl = logoUrl;

    const receiptHTML = `
        <!DOCTYPE html>
        <html dir="${isRTL ? 'rtl' : 'ltr'}">
        <head>
            <meta charset="UTF-8">
            <title>${isRefund ? t('refundReceipt') : t('paymentReceipt')} - ${invoice.receiptNumber || ''}</title>
            <style>
                @font-face {
                    font-family: 'Sped';
                    src: url('${window.location.origin}/Speda.ttf') format('truetype');
                    font-weight: normal;
                    font-style: normal;
                }

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                @media print {
                    @page { 
                        size: A4;
                        margin: 0;
                    }
                    body { 
                        margin: 0;
                        padding: 0;
                    }
                }

                body {
                    margin: 0;
                    padding: 0;
                    font-family: 'Sped', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    background: #f0f4f8;
                    direction: ${isRTL ? 'rtl' : 'ltr'};
                }

                .invoice-container {
                    max-width: 210mm;
                    min-height: 297mm;
                    margin: 0 auto;
                    background: white;
                    position: relative;
                }

                /* Blue Triangle Decoration - Top Left */
                .triangle-top {
                    position: absolute;
                    top: 0;
                    ${isRTL ? 'right' : 'left'}: 0;
                    width: 0;
                    height: 0;
                    border-top: 180px solid #2C5F9E;
                    border-right: 180px solid transparent;
                    z-index: 1;
                }

                /* Orange Triangle Decoration - Bottom Right */
                .triangle-bottom {
                    position: absolute;
                    bottom: 0;
                    ${isRTL ? 'left' : 'right'}: 0;
                    width: 0;
                    height: 0;
                    border-bottom: 200px solid #F4A261;
                    border-left: 200px solid transparent;
                    z-index: 1;
                }

                /* Logo Watermark */
                .logo-watermark {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    opacity: 0.05;
                    z-index: 0;
                    max-width: 400px;
                    max-height: 400px;
                }

                /* Header Section */
                .invoice-header {
                    padding: 35px 40px 20px;
                    position: relative;
                    z-index: 2;
                }

                .header-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                }

                .company-logo-section {
                    position: absolute;
                    left: 50%;
                    transform: translateX(-50%);
                    top: 35px;
                }

                .company-logo {
                    max-height: 65px;
                    max-width: 180px;
                    object-fit: contain;
                    display: block;
                    margin: 0 auto;
                }

                .logo-placeholder {
                    width: 60px;
                    height: 60px;
                    background: #2C5F9E;
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 28px;
                    font-weight: 700;
                    border-radius: 4px;
                    margin: 0 auto;
                }

                .invoice-title {
                    position: absolute;
                    top: 25px;
                    left: 40px;
                    z-index: 3;
                }

                .invoice-title h1 {
                    font-size: 28px;
                    font-weight: 700;
                    color: white;
                    margin-bottom: 4px;
                }

                .invoice-subtitle {
                    font-size: 13px;
                    color: rgba(255, 255, 255, 0.95);
                }

                /* Invoice Details Section */
                .invoice-details {
                    padding: 0 40px;
                    margin-top: 25px;
                }

                .details-row {
                    display: flex;
                    justify-content: space-between;
                    gap: 30px;
                    margin-bottom: 20px;
                }

                .detail-box {
                    flex: 1;
                }

                .detail-box h3 {
                    font-size: 12px;
                    font-weight: 600;
                    color: #64748b;
                    text-transform: uppercase;
                    margin-bottom: 8px;
                }

                .detail-box p {
                    font-size: 13px;
                    color: #1e293b;
                    line-height: 1.5;
                    margin: 3px 0;
                }

                .detail-box p strong {
                    font-weight: 600;
                }

                /* Items Table */
                .invoice-table {
                    margin: 20px 40px;
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                }

                thead {
                    background: #f8fafc;
                }

                thead th {
                    padding: 10px;
                    text-align: ${isRTL ? 'right' : 'left'};
                    font-size: 11px;
                    font-weight: 600;
                    color: #F4A261;
                    text-transform: uppercase;
                    border-bottom: 2px solid #e2e8f0;
                }

                tbody td {
                    padding: 10px;
                    font-size: 13px;
                    color: #1e293b;
                    border-bottom: 1px solid #e2e8f0;
                }

                .text-right {
                    text-align: ${isRTL ? 'left' : 'right'};
                }

                /* Total Section */
                .total-section {
                    margin: 20px 40px;
                    display: flex;
                    justify-content: flex-end;
                }

                .total-box {
                    min-width: 300px;
                }

                .total-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 8px 0;
                    font-size: 13px;
                }

                .total-row.subtotal {
                    color: #64748b;
                }

                .total-row.final {
                    border-top: 2px solid #e2e8f0;
                    margin-top: 8px;
                    padding-top: 10px;
                    font-size: 16px;
                    font-weight: 700;
                    color: #1e293b;
                }

                .total-row.remain {
                    border-top: 2px solid #e2e8f0;
                    margin-top: 8px;
                    padding-top: 10px;
                    font-size: 16px;
                    font-weight: 700;
                    color: #dc2626;
                }

                /* Terms Section */
                .terms-section {
                    margin: 20px 40px 15px;
                    padding: 15px;
                    background: #f8fafc;
                    border-left: 4px solid #2C5F9E;
                }

                .terms-section h3 {
                    font-size: 13px;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 8px;
                }

                .terms-section p, .terms-section ul {
                    font-size: 10px;
                    color: #64748b;
                    line-height: 1.6;
                }

                .terms-section ul {
                    margin-top: 6px;
                    padding-${isRTL ? 'right' : 'left'}: 18px;
                }

                .terms-section li {
                    margin-bottom: 4px;
                }

                /* Contact Section */
                .contact-section {
                    margin: 15px 40px 30px;
                    padding: 15px;
                    background: white;
                    border-top: 1px solid #e2e8f0;
                }

                .contact-section h3 {
                    font-size: 13px;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 8px;
                }

                .contact-section p {
                    font-size: 11px;
                    color: #64748b;
                    line-height: 1.5;
                }

                /* Signature Section */
                .signature-section {
                    margin: 40px 40px 60px;
                    position: relative;
                    z-index: 2;
                }

                .signature-box {
                    display: inline-block;
                    min-width: 250px;
                }

                .signature-line {
                    border-bottom: 2px solid #1e293b;
                    padding-bottom: 80px;
                    margin-bottom: 8px;
                }

                .signature-label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #1e293b;
                    text-align: center;
                }
            </style>
        </head>
        <body>
            <div class="invoice-container">
                <!-- Logo Watermark -->
                ${watermarkUrl ? `
                    <img src="${watermarkUrl}" alt="Watermark" class="logo-watermark"/>
                ` : ''}
                
                <!-- Blue Triangle Top Left -->
                <div class="triangle-top"></div>
                
                <!-- Orange Triangle Bottom Right -->
                <div class="triangle-bottom"></div>
                
                <!-- Header -->
                <div class="invoice-header">
                    <div class="header-content">
                        <div class="invoice-title">
                            <h1>${isRefund ? (t('refundReceipt') || 'REFUND RECEIPT') : (t('invoice') || 'INVOICE')}</h1>
                            <p class="invoice-subtitle">${user?.academy?.name || t('educationInstitute') || 'Education Institute'}</p>
                        </div>
                        <div class="company-logo-section">
                            ${logoUrl ? `
                                <img src="${logoUrl}" alt="Logo" class="company-logo" style="max-width: 100px; max-height: 100px;"/>
                            ` : `
                                <div class="logo-placeholder">
                                    <svg width="60" height="60" viewBox="0 0 60 60">
                                        <rect x="10" y="10" width="15" height="15" fill="white"/>
                                        <rect x="30" y="10" width="20" height="20" fill="white"/>
                                        <rect x="10" y="30" width="20" height="20" fill="white"/>
                                        <rect x="35" y="35" width="15" height="15" fill="white"/>
                                    </svg>
                                </div>
                            `}
                        </div>
                        
                    </div>
                </div>

                <!-- Invoice Details -->
                <div class="invoice-details" style="margin-top: 150px;">
                    <div class="details-row">
                        <div class="detail-box">
                            <h3>${t('studentDetails') || 'STUDENT DETAILS'}:</h3>
                            ${studentInfo ? `
                                <p><strong>${t('name') || 'Name'}:</strong> ${studentInfo.firstName} ${studentInfo.lastName}</p>
                                ${studentInfo.phone ? `<p><strong>${t('phone') || 'Phone'}:</strong> ${studentInfo.phone}</p>` : ''}
                                ${studentInfo.dateOfBirth ? `<p><strong>${t('dateOfBirth') || 'Date of Birth'}:</strong> ${dayjs(studentInfo.dateOfBirth).format('DD-MM-YYYY')}</p>` : ''}
                                ${studentInfo.gender ? `<p><strong>${t('gender') || 'Gender'}:</strong> ${t(studentInfo.gender) || studentInfo.gender}</p>` : ''}
                                ${studentInfo.address && (studentInfo.address.street || studentInfo.address.city) ? `<p><strong>${t('address') || 'Address'}:</strong> ${[studentInfo.address.street, studentInfo.address.city, studentInfo.address.state, studentInfo.address.country].filter(Boolean).join(', ')}</p>` : ''}
                                ${studentInfo.studyingType ? `<p><strong>${t('studyingType') || 'Studying Type'}:</strong> ${t(studentInfo.studyingType.replace('-', '')) || studentInfo.studyingType}</p>` : ''}
                                ${studentInfo.classStartHour || studentInfo.classEndHour ? `<p><strong>${t('classTime') || 'Class Time'}:</strong> ${studentInfo.classStartHour || '--'} - ${studentInfo.classEndHour || '--'}</p>` : ''}
                            ` : '<p>-</p>'}
                        </div>
                        <div class="detail-box">
                            <h3>${t('invoiceDetails') || 'INVOICE DETAILS'}:</h3>
                            <p><strong>${t('invoiceNo') || 'Invoice No'}:</strong> ${invoice.receiptNumber || '-'}</p>
                            <p><strong>${t('invoiceDate') || 'Invoice Date'}:</strong> ${dayjs(invoice.date).format('DD-MM-YYYY')}</p>
                            ${invoice.paymentMethod ? `<p><strong>${t('payment') || 'Payment'}:</strong> ${t(invoice.paymentMethod) || invoice.paymentMethod}</p>` : ''}
                            ${invoice.course?.duration ? `<p><strong>${t('courseDuration') || 'Course Duration'}:</strong> ${invoice.course.duration.value} ${t(invoice.course.duration.unit) || invoice.course.duration.unit}</p>` : ''}
                        </div>
                    </div>
                </div>

                <!-- Items Table -->
                <div class="invoice-table">
                    <table>
                        <thead>
                            <tr>
                                <th>${t('date') || 'DATE'}</th>
                                <th>${t('description') || 'DESCRIPTION'}</th>
                                <th class="text-right">${t('amount') || 'AMOUNT'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>${dayjs(invoice.date).format('DD-MM-YYYY')}</td>
                                <td>
                                    ${invoice.description || invoice.category || t('paymentForCourseFee') || 'Payment for course-fee'}
                                    ${invoice.category ? `<br/><small style="color: #64748b;">${invoice.category}</small>` : ''}
                                    ${invoice.course?.name ? `<br/><small style="color: #64748b;"><strong>${t('course') || 'Course'}:</strong> ${invoice.course.name}</small>` : ''}
                                </td>
                                <td class="text-right">${isRefund ? `-${formatAmount(refundAmount, invoice.currency)}` : formatAmount(invoice.course?.fee?.amount, invoice.currency)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Total Section -->
                <div class="total-section">
                    <div class="total-box">
                        ${isRefund ? `
                        <div class="total-row subtotal">
                            <span style="font-weight: 600;">${t('refundedAmount') || 'Refunded Amount'}:</span>
                            <span style="font-weight: 600; color: #dc2626;">-${formatAmount(refundAmount, invoice.currency)}</span>
                        </div>
                        ${invoice.refundReason ? `
                        <div class="total-row subtotal" style="margin-top: 8px; border-top: 1px solid #e2e8f0; padding-top: 8px;">
                            <span>${t('refundReason') || 'Reason'}:</span>
                            <span>${invoice.refundReason}</span>
                        </div>
                        ` : ''}
                        ${invoice.remainingBalance !== undefined && invoice.remainingBalance !== null ? `
                        <div class="total-row remain">
                            <span>${t('remainAmount') || 'Remaining Amount'}</span>
                            <span>${formatAmount(invoice.remainingBalance, invoice.currency)}</span>
                        </div>
                        ` : ''}
                        ` : invoice.course?.discount > 0 ? `
                        <div class="total-row subtotal">
                            <span>${t('totalAfterDiscount') || 'Course Discount'}:</span>
                            <span style="color: #dc2626;">${formatAmount((invoice?.originalAmount), invoice.currency)}</span>
                        </div>

                        ` : `
                        <div class="total-row subtotal">
                            <span>${t('total') || 'Course Fee'}:</span>
                            <span>${formatAmount(invoice.originalAmount, invoice.currency)}</span>
                        </div>
                        `}
                        ${invoice.discount && invoice.discount > 0 ? `
                        <div class="total-row subtotal" style="margin-top: 8px; border-top: 1px solid #e2e8f0; padding-top: 8px;">
                            <span>${t('discount') || 'Payment Discount'}:</span>
                            <span style="color: #dc2626;">-${formatAmount(invoice.discount, invoice.currency)}</span>
                        </div>
                        ` : ''}
                        <div class="total-row subtotal" style="margin-top: 8px;">
                            <span style="font-weight: 600;">${t('paymentAmount') || 'Paid in This Invoice'}:</span>
                            <span style="font-weight: 600; color: #15803d;">${formatAmount(invoice.amount, invoice.currency)}</span>
                        </div>
                        ${invoice.course?.discount > 0 ? `
                        <div class="total-row final">
                            <span>${t('remainAmount') || 'Final Amount After Course Discount'}:</span>
                            <span>${formatAmount(((invoice?.originalAmount) - ((invoice.discount + invoice.amount) || 0)), invoice.currency)}</span>
                        </div>
                        ` : `
                        <div class="total-row remain">
                            <span>${t('remainAmount') || 'Remaining Amount'}</span>
                            <span>${formatAmount((invoice.originalAmount - (invoice.discount + invoice.amount)) || 0, invoice.currency)}</span>
                        </div>`}
                        
                    </div>
                </div>

                <!-- Terms & Conditions -->
                <div class="terms-section">
                    <h3>${t('termsAndConditions') || 'Terms & Conditions'}</h3>
                    <ul>
                        <li>${t('term1') || 'The amount due for your course will be as stated in this letter you received from us.'}.</li>
                        <li>${t('term2') || 'In order to register for your course, at least 30% of the course fee must be paid. The remaining 70% of fees must be paid at least 4 weeks before the start of the course.'}.</li>
                        <li>${t('term3') || 'All fees paid are non-refundable and non-transferable under any circumstances.'}.</li>
                    </ul>
                </div>

                <!-- Contact Us -->
                <div class="contact-section">
                    <h3>${t('address') || 'ADDRESS'}</h3>
                    <p>${user?.academy?.address?.street || 'ناونیشان'}</p>
                    <h3>${t('contactUs') || 'CONTACT US'}</h3>
                    <p>${t('advisoryTelephone') || 'Advisory Telephone'}: ${user?.academy?.phone || '+72 212 8478'}</p>
                    ${user?.academy?.website ? `<p>${t('website') || 'Website'}: ${user.academy.website}</p>` : ''}
                    ${user?.academy?.socialMedia && (user?.academy?.socialMedia?.facebook || user?.academy?.socialMedia?.instagram || user?.academy?.socialMedia?.twitter || user?.academy?.socialMedia?.linkedin || user?.academy?.socialMedia?.youtube || user?.academy?.socialMedia?.telegram || user?.academy?.socialMedia?.whatsapp) ? `
                        <div style="margin-top: 16px;">
                            <p style="font-weight: 600; margin-bottom: 8px;">${t('followUs') || 'Follow Us'}:</p>
                            ${user?.academy?.socialMedia?.facebook ? `<p>Facebook: @${user.academy.socialMedia.facebook}</p>` : ''}
                            ${user?.academy?.socialMedia?.instagram ? `<p>Instagram: @${user.academy.socialMedia.instagram}</p>` : ''}
                            ${user?.academy?.socialMedia?.twitter ? `<p>Twitter: @${user.academy.socialMedia.twitter}</p>` : ''}
                            ${user?.academy?.socialMedia?.linkedin ? `<p>LinkedIn: ${user.academy.socialMedia.linkedin}</p>` : ''}
                            ${user?.academy?.socialMedia?.youtube ? `<p>YouTube: @${user.academy.socialMedia.youtube}</p>` : ''}
                            ${user?.academy?.socialMedia?.telegram ? `<p>Telegram: @${user.academy.socialMedia.telegram}</p>` : ''}
                            ${user?.academy?.socialMedia?.whatsapp ? `<p>WhatsApp: ${user.academy.socialMedia.whatsapp}</p>` : ''}
                        </div>
                    ` : ''}
                </div>

                <!-- Student Signature -->
                <div class="signature-section">
                    <div class="signature-box">
                        <div class="signature-line"></div>
                        <div class="signature-label">${t('studentSignature') || 'Student Signature'}</div>
                    </div>
                </div>
            </div>
        </body>
        </html>
    `;

    // Helper function to format amount
    function formatAmount(amount, currency = 'USD') {
        if (currency === 'IQD') {
            return `IQD ${amount.toLocaleString()}`;
        }
        return `$${amount.toFixed(2)}`;
    }

    // Create and print
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'absolute';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = 'none';
    document.body.appendChild(printFrame);

    const frameDoc = printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(receiptHTML);
    frameDoc.close();

    printFrame.onload = () => {
        const images = printFrame.contentWindow.document.images;
        const imagePromises = Array.from(images).map(img => {
            if (img.complete) return Promise.resolve();
            return new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
            });
        });

        Promise.all(imagePromises).then(() => {
            setTimeout(() => {
                printFrame.contentWindow.focus();
                printFrame.contentWindow.print();
                setTimeout(() => {
                    document.body.removeChild(printFrame);
                }, 1000);
            }, 100);
        });
    };
};
